/**
 * Auto-create a first trip from a marketing template deeplink after onboarding.
 */

import {collection, getDocs, limit, query, where} from "firebase/firestore";
import {db} from "@/lib/firebase";
import {normalizeTemplateTopicParam} from "@/navigation/templateDeepLink";
import {isTripCountedAsActiveForFreeLimit} from "@/services/entitlements";
import {inferDefaultTemplateStartDate} from "@/services/templateDefaultStartDate";
import {createTripFromTemplate, getTemplateById} from "@/services/tripTemplates";
import type {TripTemplate} from "@/types/tripTemplates";

export type AutoCreateFirstTripResult =
  | {ok: true; tripId: string}
  | {ok: false; reason: "template_not_found" | "existing_trips" | "create_failed"; message?: string};

function resolveTopicTag(template: TripTemplate, topicTag?: string): string | undefined {
  const normalized = normalizeTemplateTopicParam(topicTag);
  if (normalized) {
    return normalized;
  }
  for (const tag of template.topicTags ?? template.tags ?? []) {
    const fromTag = normalizeTemplateTopicParam(tag);
    if (fromTag) {
      return fromTag;
    }
  }
  return undefined;
}

async function userHasActiveOwnedTrip(userId: string): Promise<boolean> {
  const tripsQuery = query(collection(db, "trips"), where("owner", "==", userId), limit(8));
  const snapshot = await getDocs(tripsQuery);
  const now = new Date();
  return snapshot.docs.some((tripDoc) => isTripCountedAsActiveForFreeLimit(tripDoc.data(), now));
}

export async function tryAutoCreateFirstTripFromTemplate(args: {
  userId: string;
  templateId: string;
  topicTag?: string;
  ref?: string;
}): Promise<AutoCreateFirstTripResult> {
  try {
    if (await userHasActiveOwnedTrip(args.userId)) {
      return {ok: false, reason: "existing_trips"};
    }

    const template = await getTemplateById(args.templateId);
    if (!template) {
      return {ok: false, reason: "template_not_found"};
    }

    const topicTag = resolveTopicTag(template, args.topicTag);
    const startDate = inferDefaultTemplateStartDate(template, topicTag);
    const tripId = await createTripFromTemplate(
      args.userId,
      template.id,
      {startDate, name: template.title},
      {
        source: "deeplink",
        topicTag,
      },
    );

    return {ok: true, tripId};
  } catch (error) {
    return {
      ok: false,
      reason: "create_failed",
      message: error instanceof Error ? error.message : String(error),
    };
  }
}
